import { useEffect, useMemo } from 'react';
import { useWorldStore } from '../state/worldStore';
import { listWorldObjects } from '../services/worldObjects';
import { ROOMS } from '../rooms';

// useRoomPlaceables — one room's placed things of one kind, rows-over-seeds.
//
// The record store's sleeves proved the pattern (SplatOverlay): a saved
// arrangement in `world_objects` wins; the room's authored rooms.js config is
// only the seed, so a room ships furnished before anyone has arranged it live.
// This hook is that pattern extracted, so EVERY placeable kind rides it —
// video panels, wall records, result cards — instead of each one keeping its
// own localStorage-and-paste editor (the bad design this replaces).
//
// Rows land in the store's worldObjects slice, which is also what the
// Placeables tool edits — so a nudge in the tool re-renders the real thing
// live, and Save is the arrangement.
//
// KINDS (zone = the ROOMS key):
//   video_panel   the room's live/cameo screen — size = panel height,
//                 data.billboard = faces-the-viewer
//   plaque        the studio's wall of gold records
//   result_card   the takes in front of the console
//   card          the record store's sleeves (already migrated; listed for
//                 completeness — its shelf semantics stay in RecordCardsPanel)

// Authored rooms.js config → row-shaped seeds, per kind. A seed's object_id is
// a string ('authored:…'), which is how the save path knows to POST not PATCH.
export function seedsFor(zoneId, kind) {
  const room = ROOMS[zoneId] || {};
  const rowify = (arr, extra = () => null) => (arr || []).map((c, i) => ({
    object_id: `authored:${kind}:${i}`, zone_id: zoneId, kind, ref: null, slot: i,
    pos: [...c.pos], rotY: c.rotY || 0, size: c.size ?? c.height ?? 1, data: extra(c),
  }));
  if (kind === 'video_panel') {
    return room.videoPanel
      ? rowify([room.videoPanel], (c) => ({ billboard: c.billboard ?? true }))
      : [];
  }
  if (kind === 'plaque') return rowify(room.plaques);
  if (kind === 'result_card') return rowify(room.resultCards);
  if (kind === 'card') return rowify(room.cards);
  return [];
}

/** Load rows-or-seeds for zone+kind into the slice; return them sorted. */
export default function useRoomPlaceables(zoneId, kind) {
  const objects = useWorldStore((s) => s.worldObjects);
  const setObjects = useWorldStore((s) => s.loadWorldObjectSlice);

  useEffect(() => {
    if (!zoneId) return undefined;
    let dead = false;
    listWorldObjects(zoneId, kind).then((rows) => {
      if (dead) return;
      setObjects(zoneId, kind, rows.length ? rows : seedsFor(zoneId, kind));
    });
    return () => { dead = true; };
  }, [zoneId, kind, setObjects]);

  return useMemo(
    () => objects
      .filter((o) => o.zone_id === zoneId && o.kind === kind)
      .sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0)),
    [objects, zoneId, kind],
  );
}
